import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, LogOut, Package, RotateCcw, Star, UserRound } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useCustomer } from "../lib/auth";
import { useSEO } from "../lib/useSEO";
import { fmt } from "../data/site";

const inputCls = "w-full rounded-xl border border-inkline bg-parchment px-4 py-3 text-sm text-ink outline-none transition-colors placeholder:text-muted2/70 focus:border-spice";
const btnPrimary = "inline-flex w-full items-center justify-center gap-2 rounded-full bg-spice px-6 py-3.5 text-sm font-bold text-parchment transition-colors hover:bg-ink disabled:opacity-60";

const GoogleButton = ({ onClick }) => (
  <button type="button" onClick={onClick} data-testid="account-google-btn"
    className="inline-flex w-full items-center justify-center gap-3 rounded-full border border-inkline bg-cream px-6 py-3.5 text-sm font-semibold text-ink transition-colors hover:border-spice">
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true"><path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.5l6.7-6.7C35.6 2.6 30.2 0 24 0 14.6 0 6.5 5.4 2.6 13.2l7.8 6C12.3 13.4 17.7 9.5 24 9.5z"/><path fill="#4285F4" d="M46.5 24.5c0-1.6-.1-2.8-.4-4.1H24v7.8h12.9c-.3 2.2-1.7 5.4-4.9 7.6l7.5 5.8c4.5-4.1 7-10.2 7-17.1z"/><path fill="#FBBC05" d="M10.4 28.8A14.5 14.5 0 0 1 9.5 24c0-1.7.3-3.3.8-4.8l-7.8-6A24 24 0 0 0 0 24c0 3.9.9 7.5 2.6 10.8l7.8-6z"/><path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.5-5.8c-2 1.4-4.8 2.4-8.4 2.4-6.3 0-11.7-4-13.6-9.9l-7.8 6C6.5 42.6 14.6 48 24 48z"/></svg>
    Continue with Google
  </button>
);

const AuthCard = () => {
  const { login, register, loginWithGoogle } = useCustomer();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") await login(form.email, form.password);
      else await register(form.name, form.email, form.password);
      toast.success(mode === "login" ? "Welcome back!" : "Your account is ready.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md rounded-3xl border border-inkline bg-cream p-8 sm:p-10" data-testid="account-auth-card">
      <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-spice">My HiPlate</p>
      <h1 className="mt-3 font-display text-3xl font-semibold text-ink sm:text-4xl">{mode === "login" ? "Welcome back." : "Create your account."}</h1>
      <p className="mt-3 text-sm leading-relaxed text-muted2">See past orders, reorder your favorite bundle in one tap, and keep your delivery details saved.</p>
      <div className="mt-6"><GoogleButton onClick={loginWithGoogle} /></div>
      <div className="my-5 flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.25em] text-muted2"><span className="h-px flex-1 bg-inkline" />or with email<span className="h-px flex-1 bg-inkline" /></div>
      <form onSubmit={submit} className="space-y-3" data-testid="account-auth-form">
        {mode === "register" && <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" required minLength={2} className={inputCls} data-testid="account-name-input" />}
        <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" required className={inputCls} data-testid="account-email-input" />
        <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={mode === "login" ? "Password" : "Password (8+ characters)"} required minLength={mode === "login" ? 1 : 8} className={inputCls} data-testid="account-password-input" />
        <button type="submit" disabled={busy} className={btnPrimary} data-testid="account-submit-btn">
          {busy && <Loader2 size={16} className="animate-spin" />} {mode === "login" ? "Sign in" : "Create account"}
        </button>
      </form>
      <button type="button" onClick={() => setMode(mode === "login" ? "register" : "login")} className="mt-5 text-sm text-ink/70 underline-offset-4 hover:text-spice hover:underline" data-testid="account-toggle-mode">
        {mode === "login" ? "New here? Create an account" : "Already have an account? Sign in"}
      </button>
    </motion.div>
  );
};

const STATUS_TONE = { Delivered: "bg-halal/10 text-halal", Cancelled: "bg-spice/10 text-spice" };

const OrderCard = ({ order, onReorder }) => (
  <div className="rounded-2xl border border-inkline bg-cream p-5" data-testid={`account-order-${order.id.slice(0, 8)}`}>
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted2">#{order.id.slice(0, 8).toUpperCase()} · {new Date(order.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}</div>
        <div className="mt-1 font-display text-lg font-semibold text-ink">{order.meal_count} meals · {fmt(order.total)}</div>
      </div>
      <span className={`rounded-full px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-wider ${STATUS_TONE[order.status] || "bg-saffron/15 text-ink/80"}`} data-testid={`account-order-status-${order.id.slice(0, 8)}`}>{order.status}</span>
    </div>
    <ul className="mt-3 space-y-1 text-sm text-ink/80">
      {order.items.map((i, idx) => <li key={idx}>{i.qty}× {i.name}</li>)}
    </ul>
    <div className="mt-4 flex flex-wrap gap-2">
      <button onClick={() => onReorder(order)} className="inline-flex items-center gap-2 rounded-full bg-ink px-4 py-2 text-xs font-semibold text-parchment transition-colors hover:bg-spice" data-testid={`account-reorder-${order.id.slice(0, 8)}`}>
        <RotateCcw size={13} /> Reorder this bundle
      </button>
      {order.review_token && !order.review_submitted && (
        <Link to={`/review/${order.review_token}`} className="inline-flex items-center gap-2 rounded-full border border-inkline px-4 py-2 text-xs font-semibold text-ink transition-colors hover:border-spice hover:text-spice" data-testid={`account-review-${order.id.slice(0, 8)}`}>
          <Star size={13} /> Leave a review
        </Link>
      )}
    </div>
  </div>
);

const Dashboard = () => {
  const { user, logout, setUser } = useCustomer();
  const navigate = useNavigate();
  const [orders, setOrders] = useState(null);
  const [profile, setProfile] = useState({ name: user.name, phone: user.phone, address: user.address, city: user.city, zip_code: user.zip_code });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/account/orders").then(({ data }) => setOrders(data)).catch((err) => toast.error(formatApiError(err)));
  }, []);

  const saveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.patch("/account/profile", profile);
      setUser(data);
      toast.success("Delivery details saved.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const reorder = (order) => navigate("/meal-prep", { state: { reorder: order.items } });

  return (
    <div className="mx-auto w-full max-w-5xl" data-testid="account-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-spice">My HiPlate</p>
          <h1 className="mt-3 font-display text-3xl font-semibold text-ink sm:text-4xl" data-testid="account-greeting">Hi, {user.name.split(" ")[0]}.</h1>
          <p className="mt-2 text-sm text-muted2">{user.email}</p>
        </div>
        <button onClick={() => logout().then(() => toast.success("Signed out."))} className="inline-flex items-center gap-2 rounded-full border border-inkline px-4 py-2 text-xs font-semibold text-ink transition-colors hover:border-spice hover:text-spice" data-testid="account-logout-btn">
          <LogOut size={13} /> Sign out
        </button>
      </div>

      <div className="mt-10 grid gap-8 lg:grid-cols-[1fr_340px]">
        <section>
          <h2 className="flex items-center gap-2 font-display text-lg font-semibold text-ink"><Package size={18} className="text-spice" /> Order history</h2>
          <div className="mt-4 space-y-4">
            {orders === null && <div className="rounded-2xl border border-inkline bg-cream p-8 text-center text-sm text-muted2">Loading…</div>}
            {orders?.length === 0 && (
              <div className="rounded-2xl border border-dashed border-inkline bg-cream p-8 text-center" data-testid="account-no-orders">
                <p className="text-sm text-muted2">No orders yet under {user.email}.</p>
                <Link to="/meal-prep" className="mt-4 inline-block rounded-full bg-spice px-6 py-3 text-sm font-bold text-parchment transition-colors hover:bg-ink" data-testid="account-first-order-cta">Build your first bundle</Link>
              </div>
            )}
            {orders?.map((o) => <OrderCard key={o.id} order={o} onReorder={reorder} />)}
          </div>
        </section>

        <aside>
          <h2 className="flex items-center gap-2 font-display text-lg font-semibold text-ink"><UserRound size={18} className="text-spice" /> Delivery details</h2>
          <form onSubmit={saveProfile} className="mt-4 space-y-3 rounded-2xl border border-inkline bg-cream p-5" data-testid="account-profile-form">
            <input value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} placeholder="Full name" required minLength={2} className={inputCls} data-testid="profile-name-input" />
            <input value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} placeholder="Phone" className={inputCls} data-testid="profile-phone-input" />
            <input value={profile.address} onChange={(e) => setProfile({ ...profile, address: e.target.value })} placeholder="Street address" className={inputCls} data-testid="profile-address-input" />
            <div className="grid grid-cols-[1fr_110px] gap-3">
              <input value={profile.city} onChange={(e) => setProfile({ ...profile, city: e.target.value })} placeholder="City" className={inputCls} data-testid="profile-city-input" />
              <input value={profile.zip_code} onChange={(e) => setProfile({ ...profile, zip_code: e.target.value })} placeholder="ZIP" className={inputCls} data-testid="profile-zip-input" />
            </div>
            <button type="submit" disabled={saving} className={btnPrimary} data-testid="profile-save-btn">{saving && <Loader2 size={16} className="animate-spin" />} Save details</button>
            <p className="text-xs leading-relaxed text-muted2">We pre-fill these at checkout so ordering takes seconds.</p>
          </form>
        </aside>
      </div>
    </div>
  );
};

export default function Account() {
  const { user } = useCustomer();
  useSEO({ title: "My Account — HiPlate", description: "Sign in to see your HiPlate orders, reorder your favorite halal meal bundle, and manage delivery details.", path: "/account" });

  return (
    <main className="grain flex min-h-[85vh] justify-center bg-parchment px-4 pt-32 pb-24 sm:px-8" data-testid="account-page">
      {user === undefined ? <Loader2 size={36} className="mt-20 animate-spin text-spice" /> : user ? <Dashboard /> : <AuthCard />}
    </main>
  );
}
